import delay from '../../utils/delay'

import { resultError, resultSuccess } from '../../interfaces/base'
import { GetCampaigns } from '../../interfaces/services/campaigns'
import { GetClient, GetClients } from '../../interfaces/services/clients'
import {
  AssignProjectAccessToUser,
  CopyProject,
  CopyProjectPermissionsFromUser,
  CreateFormatset,
  DoProjectAction,
  GetFormatsets,
  GetPlatforms,
  GetProjectForPreview,
  GetProjects,
  GetProjectStatuses,
  GetProjectsUserHasAccessTo,
  ImportFormatsets,
  RemoveFormatSet,
  SaveProject,
  SearchProjects,
  SendEmail,
  IProjectService,
  SetProjetcsComment,
  ToggleFavoriteProject,
  UpdateFormatset,
  UpdateFormatsetPosition,
} from '../../interfaces/services/projects'

import { IEmail } from '../../interfaces/entities/email'
import {
  isSavedProject,
  ProjectId,
  ProjectStatus,
} from '../../interfaces/entities/project'
import { advertiserUsers as mockedAdvertiserUsers } from '../../mocks/advertiserUser'
import { campaigns as mockedCampaigns } from '../../mocks/campaigns'
import { clients as mockedClients } from '../../mocks/clients'
import { formatsets as mockedFormatsets } from '../../mocks/formatsets'
import { platformsMocked } from '../../mocks/platforms'
import {
  projects as mockedProjects,
  projectStatuses as mockedProjectStatuses,
  projectStatuses,
} from '../../mocks/projects'
import { shuffle } from '../../utils/array'
import { translateWithUserLocale } from '../../utils/i18n'

export const projectServiceMock: (di: any) => IProjectService = (di) => {
  const getClients: GetClients = async () => {
    await delay(1000)
    return resultSuccess(mockedClients)
  }

  const getProjects: GetProjects = async () => {
    await delay(1000)
    return resultSuccess(mockedProjects)
  }

  const getProjectForPreview: GetProjectForPreview = async (id) => {
    await delay(1000)
    return resultSuccess(mockedProjects)
  }

  const getProjectsUserHasAccessTo: GetProjectsUserHasAccessTo = async () => {
    await delay(1000)
    return resultSuccess(mockedProjects)
  }

  const copyProject: CopyProject = async () => {
    await delay(1000)
    return resultSuccess(mockedProjects)
  }

  const searchProjects: SearchProjects = async (filter) => {
    await delay(1000)
    return resultSuccess({
      projects: mockedProjects,
      pagination: {
        page: 1,
        totalRows: 1,
        next: null,
        previous: null,
      },
    })
  }

  const getProject: (projectId: ProjectId) => any = async (
    projectId: ProjectId,
  ) => {
    await delay(1000)
    try {
      const project = mockedProjects.find((p) => p.id === projectId)
      if (project !== undefined) {
        return resultSuccess(project)
      } else {
        throw new Error(translateWithUserLocale('error.not_found'))
      }
    } catch (e) {
      return resultError(e)
    }
  }

  const saveProject: SaveProject = async (project) => {
    await delay(1000)
    const item =
      mockedProjects[Math.floor(Math.random() * mockedProjects.length)]
    return resultSuccess({ project: item, created: !isSavedProject(project) })
  }

  const getClient: GetClient = async (
    clientId,
    advertiserContacts,
    projectId,
  ) => {
    await delay(1000)

    const client =
      mockedClients.find((c) => c.id === clientId) || mockedClients[0]

    return resultSuccess(client)
  }

  const getProjectStatuses: GetProjectStatuses = async () => {
    return resultSuccess(mockedProjectStatuses)
  }

  const getCampaigns: GetCampaigns = async (client) => {
    await delay(1000)

    const campaigns = shuffle(mockedCampaigns)

    return resultSuccess([campaigns[0], campaigns[1], campaigns[2]])
  }

  const doProjectAction: DoProjectAction = async (action, project, options) => {
    await delay(1000)

    const projectCopy = {
      ...project,
      status: projectStatuses.find(
        (ps) => ps.name !== project.status.name,
      ) as ProjectStatus,
    }

    return resultSuccess(projectCopy)
  }

  const getFormatsets: GetFormatsets = async () => {
    await delay(1000)
    return resultSuccess(mockedFormatsets)
  }

  const createFormatset: CreateFormatset = async (params) => {
    await delay(1000)

    return resultSuccess(mockedFormatsets[0])
  }

  const updateFormatset: UpdateFormatset = async (params) => {
    await delay(1000)

    return resultSuccess(mockedFormatsets[0])
  }

  const importFormatsets: ImportFormatsets = async (params) => {
    await delay(1000)

    return resultSuccess({ invalidFormatsets: [], result: true })
  }

  const updateFormatsetPosition: UpdateFormatsetPosition = async (params) => {
    await delay(1000)

    return resultSuccess({ ...mockedFormatsets[0] })
  }

  const removeFormatset: RemoveFormatSet = async (formatsetId) => {
    await delay(1000)

    const projectIndex = mockedProjects.findIndex((project) => {
      return (
        project.formatsets.findIndex(
          (formatset) => formatset.id !== formatsetId,
        ) !== -1
      )
    })

    if (projectIndex !== -1) {
      return resultSuccess({
        ...mockedProjects[projectIndex],
        formatsets: mockedFormatsets.filter(
          (formatset) => formatset.id !== formatsetId,
        ),
      })
    } else {
      return resultError({
        title: translateWithUserLocale('error.project_not_found_title'),
        message: translateWithUserLocale('error.project_not_found_message'),
        statusCode: '404',
      })
    }
  }

  const setProjectComment: SetProjetcsComment = async (
    projectId: ProjectId,
    comment: string,
  ) => {
    await delay(1000)

    const projectIndex = mockedProjects.findIndex(
      (project) => project.id === projectId,
    )
    if (projectIndex !== -1) {
      return resultSuccess({ content: comment })
    } else {
      return resultError({
        title: translateWithUserLocale('error.project_not_found_title'),
        message: translateWithUserLocale('error.project_not_found_message'),
        statusCode: '404',
      })
    }
  }

  const sendEmail: SendEmail = async (email: IEmail) => {
    await delay(1000)
    try {
      return resultSuccess('Email sent sucessfully')
    } catch (e) {
      return resultError({
        statusCode: '500',
        title: translateWithUserLocale('whoops') + '!',
        message: translateWithUserLocale('error.send_mail'),
      })
    }
  }

  const copyProjectPermissionsFromUser: CopyProjectPermissionsFromUser = async (
    _from,
    _to,
  ) => {
    await delay(1000)

    const projects = mockedProjects
    const user = mockedAdvertiserUsers[0]

    return resultSuccess({ user, projects })
  }

  const assignProjectAccess: AssignProjectAccessToUser = async (
    _userId,
    projects,
  ) => {
    await delay(1000)

    return resultSuccess({ user: mockedAdvertiserUsers[0], projects })
  }

  const getPlatforms: GetPlatforms = async () => {
    await delay(1000)
    return resultSuccess(platformsMocked)
  }

  const toggleFavoriteProject: ToggleFavoriteProject = async (
    id,
    isFavorite,
  ) => {
    await delay(1000)
    return resultSuccess('success')
  }

  return {
    searchProjects,
    getPlatforms,
    getProjects,
    getProject,
    getProjectStatuses,
    saveProject,
    setProjectComment,
    getProjectForPreview,

    doProjectAction,

    getFormatsets,
    createFormatset,
    updateFormatset,
    removeFormatset,
    updateFormatsetPosition,
    importFormatsets,

    getCampaigns,
    getClients,
    getClient,

    sendEmail,

    copyProjectPermissionsFromUser,
    getProjectsUserHasAccessTo,
    assignProjectAccess,

    toggleFavoriteProject,
    copyProject,
  }
}

export default projectServiceMock
